/**
 * ExportImportModal Component - Export and Import interface
 *
 * This component provides a modal interface for managing trip data export and import
 * using browser-based download and upload patterns. Replaces the backup directory system.
 *
 * Features:
 * - Trip export with format selection (JSON, PDF, ZIP)
 * - Trip import with drag-and-drop file upload
 * - Selective export/import options
 * - Progress tracking and notifications
 * - Integration with browser download/upload APIs
 */

import React, { useState, useCallback, useEffect } from 'react';
import { exportService } from '../../services/exportService';
import { importService } from '../../services/importService';
import './ExportImportModal.css';

const ExportImportModal = ({ userId, tripId, isOpen, onClose, initialTab = 'export' }) => {
  // Component state
  const [activeTab, setActiveTab] = useState(initialTab);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [progress, setProgress] = useState(0);
  const [progressDetails, setProgressDetails] = useState('');

  // Export state
  const [exportFormat, setExportFormat] = useState('json');
  const [exportScope, setExportScope] = useState({
    trip: true,
    days: true,
    places: true,
    accommodation: true,
    travel: true,
    documents: true,
    extras: true
  });

  // Import state
  const [importFile, setImportFile] = useState(null);
  const [importMode, setImportMode] = useState('new'); // 'new', 'merge', 'replace'
  const [importPreview, setImportPreview] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  /**
   * Reset state when modal closes
   */
  useEffect(() => {
    if (!isOpen) {
      resetState();
    }
  }, [isOpen]);

  /**
   * Reset component state
   */
  const resetState = useCallback(() => {
    setError(null);
    setSuccess(null);
    setProgress(0);
    setProgressDetails('');
    setImportFile(null);
    setImportPreview(null);
    setIsLoading(false);
  }, []);

  /**
   * Handle export format change
   */
  const handleFormatChange = useCallback((format) => {
    setExportFormat(format);
    setError(null);
  }, []);

  /**
   * Handle export scope toggle
   */
  const handleScopeToggle = useCallback((scopeKey) => {
    setExportScope(prev => ({
      ...prev,
      [scopeKey]: !prev[scopeKey]
    }));
  }, []);

  /**
   * Handle trip export
   */
  const handleExport = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      setProgress(0);
      setProgressDetails('Preparing export...');

      // Validate inputs
      if (!tripId) {
        throw new Error('No active trip selected');
      }

      const selectedScopes = Object.keys(exportScope).filter(key => exportScope[key]);
      if (selectedScopes.length === 0) {
        throw new Error('Please select at least one data type to export');
      }

      // Progress tracking
      const progressHandler = (progressData) => {
        setProgress(progressData.progress || 0);
        setProgressDetails(progressData.details || '');
      };

      // Trigger export with browser download
      const result = await exportService.downloadTripExport({
        tripId,
        userId,
        format: exportFormat,
        scope: selectedScopes,
        onProgress: progressHandler
      });

      setSuccess(`Export completed! File downloaded: ${result.fileName}`);
      setProgress(100);
      setProgressDetails('Export complete');

    } catch (err) {
      console.error('Export failed:', err);
      setError(err.message || 'Export failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [tripId, userId, exportFormat, exportScope]);

  /**
   * Handle file selection for import
   */
  const handleFileSelect = useCallback(async (file) => {
    try {
      setImportFile(file);
      setError(null);
      setIsLoading(true);
      setProgressDetails('Analyzing file...');

      // Validate file
      const validation = await importService.validateImportFile(file);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }

      // Generate preview
      const preview = await importService.generateImportPreview(file);
      setImportPreview(preview);

    } catch (err) {
      console.error('File analysis failed:', err);
      setError(err.message || 'Failed to analyze file');
      setImportFile(null);
    } finally {
      setIsLoading(false);
      setProgressDetails('');
    }
  }, []);

  /**
   * Handle drag and drop events
   */
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  /**
   * Handle file input change
   */
  const handleFileChange = useCallback((e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  }, [handleFileSelect]);

  /**
   * Handle import execution
   */
  const handleImport = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      setProgress(0);
      setProgressDetails('Starting import...');

      if (!importFile) {
        throw new Error('No file selected for import');
      }

      // Progress tracking
      const progressHandler = (progressData) => {
        setProgress(progressData.progress || 0);
        setProgressDetails(progressData.details || '');
      };

      // Execute import
      const result = await importService.uploadTripImport({
        file: importFile,
        userId,
        importMode,
        targetTripId: importMode === 'new' ? null : tripId,
        onProgress: progressHandler
      });

      setSuccess(`Import completed! ${result.summary}`);
      setProgress(100);
      setProgressDetails('Import complete');

      // Reset import state
      setTimeout(() => {
        setImportFile(null);
        setImportPreview(null);
      }, 2000);

    } catch (err) {
      console.error('Import failed:', err);
      setError(err.message || 'Import failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [importFile, userId, importMode, tripId]);

  /**
   * Handle modal close
   */
  const handleClose = useCallback(() => {
    if (isLoading) {
      const confirmClose = window.confirm('Operation in progress. Are you sure you want to close?');
      if (!confirmClose) return;
    }
    onClose?.();
  }, [isLoading, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="export-import-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            <span className="icon">{activeTab === 'export' ? '📤' : '📥'}</span>
            {activeTab === 'export' ? 'Export Trip Data' : 'Import Trip Data'}
          </h2>
          <button
            onClick={handleClose}
            className="close-button"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="modal-tabs">
          <button
            className={`tab-button ${activeTab === 'export' ? 'active' : ''}`}
            onClick={() => setActiveTab('export')}
            disabled={isLoading}
          >
            📤 Export
          </button>
          <button
            className={`tab-button ${activeTab === 'import' ? 'active' : ''}`}
            onClick={() => setActiveTab('import')}
            disabled={isLoading}
          >
            📥 Import
          </button>
        </div>

        <div className="modal-content">
          {error && (
            <div className="error-message">
              <h4>❌ Error</h4>
              <p>{error}</p>
            </div>
          )}

          {success && (
            <div className="success-message">
              <h4>✅ Success</h4>
              <p>{success}</p>
            </div>
          )}

          {isLoading && (
            <div className="progress-section">
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <p className="progress-details">{progressDetails}</p>
            </div>
          )}

          {activeTab === 'export' && (
            <div className="export-section">
              <div className="format-selection">
                <h3>Export Format</h3>
                <div className="format-options">
                  <label className="format-option">
                    <input
                      type="radio"
                      value="json"
                      checked={exportFormat === 'json'}
                      onChange={(e) => handleFormatChange(e.target.value)}
                      disabled={isLoading}
                    />
                    <span>JSON - Complete trip data</span>
                  </label>
                  <label className="format-option">
                    <input
                      type="radio"
                      value="pdf"
                      checked={exportFormat === 'pdf'}
                      onChange={(e) => handleFormatChange(e.target.value)}
                      disabled={isLoading}
                    />
                    <span>PDF - Read-only summary</span>
                  </label>
                  <label className="format-option">
                    <input
                      type="radio"
                      value="zip"
                      checked={exportFormat === 'zip'}
                      onChange={(e) => handleFormatChange(e.target.value)}
                      disabled={isLoading}
                    />
                    <span>ZIP - JSON + media files</span>
                  </label>
                </div>
              </div>

              <div className="scope-selection">
                <h3>Export Scope</h3>
                <div className="scope-options">
                  {Object.entries(exportScope).map(([key, checked]) => (
                    <label key={key} className="scope-option">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => handleScopeToggle(key)}
                        disabled={isLoading}
                      />
                      <span>{key.charAt(0).toUpperCase() + key.slice(1)}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'import' && (
            <div className="import-section">
              {!importFile && (
                <div
                  className={`file-upload-area ${dragActive ? 'drag-active' : ''}`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  onClick={() => document.getElementById('file-input').click()}
                >
                  <input
                    id="file-input"
                    type="file"
                    accept=".json,.zip"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                  <div className="upload-content">
                    <span className="upload-icon">📁</span>
                    <h3>Drag & Drop Import File</h3>
                    <p>or click to browse files</p>
                    <p className="supported-formats">
                      Supported: JSON, ZIP (max 100MB)
                    </p>
                  </div>
                </div>
              )}

              {importFile && importPreview && (
                <div className="import-preview">
                  <div className="file-info">
                    <h3>✅ File Selected</h3>
                    <p>📄 {importFile.name}</p>
                    <p>Size: {(importFile.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>

                  <div className="trip-preview">
                    <h4>Trip Information</h4>
                    <p><strong>Name:</strong> {importPreview.tripName}</p>
                    <p><strong>Dates:</strong> {importPreview.dateRange}</p>
                    <p><strong>Exported:</strong> {importPreview.exportDate}</p>
                  </div>

                  <div className="data-summary">
                    <h4>Data Summary</h4>
                    <ul>
                      {Object.entries(importPreview.dataSummary).map(([key, count]) => (
                        <li key={key}>• {key}: {count} entries</li>
                      ))}
                    </ul>
                  </div>

                  <div className="import-mode">
                    <h4>Import Mode</h4>
                    <div className="mode-options">
                      <label className="mode-option">
                        <input
                          type="radio"
                          value="new"
                          checked={importMode === 'new'}
                          onChange={(e) => setImportMode(e.target.value)}
                          disabled={isLoading}
                        />
                        <span>Create new trip</span>
                      </label>
                      {tripId && (
                        <>
                          <label className="mode-option">
                            <input
                              type="radio"
                              value="merge"
                              checked={importMode === 'merge'}
                              onChange={(e) => setImportMode(e.target.value)}
                              disabled={isLoading}
                            />
                            <span>Merge with current trip</span>
                          </label>
                          <label className="mode-option">
                            <input
                              type="radio"
                              value="replace"
                              checked={importMode === 'replace'}
                              onChange={(e) => setImportMode(e.target.value)}
                              disabled={isLoading}
                            />
                            <span>Replace current trip</span>
                          </label>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="file-actions">
                    <button
                      onClick={() => setImportFile(null)}
                      className="secondary-button"
                      disabled={isLoading}
                    >
                      Change File
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-actions">
          <button
            onClick={handleClose}
            className="modal-button secondary"
            disabled={isLoading}
          >
            Cancel
          </button>

          {activeTab === 'export' && (
            <button
              onClick={handleExport}
              className="modal-button primary"
              disabled={isLoading || !tripId}
            >
              {isLoading ? 'Exporting...' : 'Export'}
            </button>
          )}

          {activeTab === 'import' && importFile && (
            <button
              onClick={handleImport}
              className="modal-button primary"
              disabled={isLoading}
            >
              {isLoading ? 'Importing...' : 'Import'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExportImportModal;